import { useEffect, useMemo, useState } from "react";
import DataListPage from "./DataListPage";
import { STATUS_STYLES } from "@/data/mockData";
import { adminPaymentService } from "@/services/adminPaymentService";
import ToastHelper from "@/utils/toastHelper";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-AU");
};

const formatAudCurrency = (amount) => `$${Number(amount || 0).toFixed(2)} AUD`;

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadPayments = async () => {
      setLoading(true);
      setError("");

      try {
        const result = await adminPaymentService.getPayments();
        if (!active) return;

        const rows = (result?.payments || []).map((payment) => ({
          id: payment.paymentId || `P-${String(payment.paymentTransactionId).padStart(4, "0")}`,
          paymentTransactionId: payment.paymentTransactionId,
          studentBookingId: payment.studentBookingId,
          bookingNumber: payment.bookingNumber || "-",
          studentId: payment.studentId,
          student: payment.studentName || "-",
          studentEmail: payment.studentEmail,
          amount: formatAudCurrency(payment.amount || 0),
          currencyCode: payment.currencyCode || "AUD",
          method: payment.paymentMethod || "-",
          date: formatDate(payment.paymentDate),
          rawDate: payment.paymentDate,
          status: payment.paymentStatus || "Pending",
          receipt: payment.receiptNumber,
          invoiceNumber: payment.invoiceNumber,
          gateway: payment.paymentGateway,
          courseName: payment.courseName,
        }));

        setPayments(rows);
      } catch (requestError) {
        if (!active) return;
        const message = requestError?.response?.data?.message || "Unable to load payments.";
        setError(message);
        ToastHelper.error(message);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPayments();
    return () => {
      active = false;
    };
  }, []);

  const statusStyles = useMemo(
    () => ({
      ...STATUS_STYLES,
      Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Pending: "bg-amber-50 text-amber-700 border-amber-200",
      Failed: "bg-rose-50 text-rose-700 border-rose-200",
      Refunded: "bg-slate-100 text-slate-700 border-slate-200",
    }),
    []
  );

  if (loading) {
    return <div className="wc-card p-8 text-sm text-slate-500">Loading payments...</div>;
  }

  if (error) {
    return <div className="wc-card p-8 text-sm text-rose-600">{error}</div>;
  }

  return (
    <DataListPage
      title="Payments"
      subtitle="Transactions and refunds"
      testidPrefix="payments"
      rows={payments}
      paginated
      columnFilters
      statusStyles={statusStyles}
      actions={[
        {
          label: "View receipt",
          fn: async (row) => {
            if (!row.receipt || !row.paymentTransactionId) {
              ToastHelper.error("Receipt is not available for this payment.");
              return;
            }

            try {
              await adminPaymentService.downloadReceipt(
                row.paymentTransactionId,
                row.receipt
              );
              ToastHelper.success(`${row.receipt} downloaded.`);
            } catch (requestError) {
              const message =
                requestError?.response?.data?.message ||
                "Unable to download the receipt.";
              ToastHelper.error(message);
            }
          },
        },
        {
          label: "Refund",
          fn: (row) => ToastHelper.info(`Refund workflow for ${row.id} is not enabled yet.`),
        },
        {
          label: "Resend email",
          fn: (row) =>
            row.studentEmail
              ? ToastHelper.info(`Receipt email can be sent to ${row.studentEmail}.`)
              : ToastHelper.error("Student email is not available."),
        },
      ]}
      columns={[
        { key: "id", label: "Payment ID" },
        { key: "bookingNumber", label: "Booking No." },
        {
          key: "student",
          label: "Student",
          render: (row) => <span className="font-semibold">{row.student}</span>,
        },
        { key: "amount", label: "Amount", align: "right" },
        { key: "method", label: "Method" },
        { key: "date", label: "Date" },
        { key: "status", label: "Status", type: "status" },
        {
          key: "receipt",
          label: "Payment Reference",
          render: (row) => row.receipt || "-",
        },
      ]}
    />
  );
}
